import { isEqual, sortBy } from 'lodash'

import { SignersList } from '~/components/SignersList'
import { ThresholdSelect } from '~/components/account-detail/AccountActionsMenu/ThresholdSelect'
import { useSaveSmartAccountSigners } from '~/components/account-detail/AccountActionsMenu/useSaveSmartAccountSigners'
import { useSignerSelection } from '~/components/account-detail/AccountActionsMenu/useSignerSelection'
import { SplitsDialogContent } from '~/components/component-library/Dialog'
import { OrgSmartAccount } from '~/types'
import { sendTrackingData } from '~/utils/tracking'

export const EditSignersDialog = ({
  smartAccount,
  closeDialog,
}: {
  smartAccount: OrgSmartAccount
  closeDialog: () => void
}) => {
  const { save, isProcessing } = useSaveSmartAccountSigners({
    smartAccount,
    isRecoveryTx: false,
  })
  const {
    org,
    selectedSigners,
    threshold,
    setThreshold,
    availableSigners,
    setSigner,
    handleAddNewPasskey,
  } = useSignerSelection({
    baseSigners: smartAccount.signers,
    initialSigners: smartAccount.signers,
    initialThreshold: smartAccount.threshold,
  })

  const onSave = async () => {
    sendTrackingData('clickedUpdateSigners', {
      smartAccountId: smartAccount.id,
    })
    const result = await save({ signers: selectedSigners, threshold })
    if (result) closeDialog()
  }

  const signersChanged = !isEqual(
    sortBy(smartAccount.signers.map((signer) => signer.id)),
    sortBy(selectedSigners.map((signer) => signer.id)),
  )
  const hasChanges = signersChanged || threshold !== smartAccount.threshold
  const thresholdTooHigh = threshold > selectedSigners.length
  const isValid = threshold > 0 && !thresholdTooHigh
  const isSaveDisabled = !hasChanges || !isValid || isProcessing

  return (
    <SplitsDialogContent
      id="editSmartAccountSigners"
      title="Edit signers"
      variant="cta"
      isLoading={isProcessing}
      actionButton={{
        text: 'Save',
        onClick: () => void onSave(),
        isDisabled: isSaveDisabled,
        disabledReason: thresholdTooHigh
          ? 'Your threshold cannot be greater than the number of selected signers'
          : undefined,
      }}
    >
      <SignersList
        header="Signers"
        signers={availableSigners}
        eoaSigners={smartAccount.eoaSigners}
        initialExpanded={org?.members.length === 1}
        editable={{ selectedSigners, onChange: setSigner }}
        onAddNewPasskey={handleAddNewPasskey}
      />
      <ThresholdSelect
        signerCount={selectedSigners.length}
        threshold={threshold}
        setThreshold={setThreshold}
      />
    </SplitsDialogContent>
  )
}
